import type { MetricSummary, SliceMetrics } from '@/analysis/searchContract'
import { getSevColor } from '@/constants'

/**
 * Queue/buffer bullet bars — the leading indicators of stress (queues back up
 * before frames drop). Each bullet shows the current value against a
 * good/watch/bad band. Sources are the aggregated buffer metrics carried on
 * the stream node; raw queue lengths (workerQueueLen/renderQueueLen) are not
 * persisted per-window, so buffer level + decode stage time stand in as the
 * queue-pressure signals, honestly labeled.
 */

type Bullet = {
  key: string
  label: string
  value: number | null
  tier: 0 | 1 | 2 | null
  /** Upper bound used to scale the bar. */
  scaleMax: number
  fmt: (v: number) => string
  /** true = high is bad (stall ms); false = low is bad (buffer level). */
  highIsBad: boolean
  title?: string
}

const num = (m: MetricSummary | undefined | null): number | null =>
  typeof m?.displayValue === 'number' && Number.isFinite(m.displayValue) ? m.displayValue : null

const tierOf = (m: MetricSummary | undefined | null): 0 | 1 | 2 | null =>
  m?.tier === 0 || m?.tier === 1 || m?.tier === 2 ? m.tier : null

const ms = (v: number) => (v < 10 ? `${v.toFixed(1)}ms` : `${Math.round(v)}ms`)

export function buildBullets(metrics: SliceMetrics | null | undefined): Bullet[] {
  const m = metrics ?? ({} as SliceMetrics)
  return [
    {
      key: 'bufferLevel',
      label: 'Buffer level',
      value: num(m.bufferLevel),
      tier: tierOf(m.bufferLevel),
      scaleMax: 4000,
      fmt: ms,
      highIsBad: false,
      title: 'Decode buffer level (window minimum). Dips below ~1s risk an under-run.',
    },
    {
      key: 'decodeStage',
      label: 'Decode stage',
      value: num(m.decodeQueue) ?? num(m.packetDelay),
      tier: tierOf(m.decodeQueue) ?? tierOf(m.packetDelay),
      scaleMax: 100,
      fmt: ms,
      highIsBad: true,
      title: 'Per-frame decode queue+worker time. Rising values = decode backlog building.',
    },
    {
      key: 'underRun',
      label: 'Under-run',
      value: num(m.bufferUnderRunMs),
      tier: tierOf(m.bufferUnderRunMs),
      scaleMax: 1000,
      fmt: ms,
      highIsBad: true,
      title: 'Render stall time this window (renderStallMs). Any non-zero value is visible stutter.',
    },
    {
      key: 'overRun',
      label: 'Over-run',
      value: num(m.bufferOverRun),
      tier: tierOf(m.bufferOverRun),
      scaleMax: 10,
      fmt: (v) => `${Math.round(v)}×`,
      highIsBad: true,
      title: 'Render-queue high-watermark breaches (v2 telemetry) — frames arriving faster than render.',
    },
  ]
}

function bulletColor(b: Bullet): string {
  if (b.tier != null) return getSevColor(b.tier)
  if (b.value == null) return 'var(--bd-1)'
  const frac = Math.min(1, b.value / b.scaleMax)
  const bad = b.highIsBad ? frac : 1 - frac
  return bad > 0.66 ? getSevColor(0) : bad > 0.33 ? getSevColor(1) : getSevColor(2)
}

export default function QueueBullets({ metrics }: { metrics: SliceMetrics | null | undefined }) {
  const bullets = buildBullets(metrics)
  return (
    <div style={{ border: '0.5px solid var(--bd-1)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--t-1)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
        Queue / buffer pressure
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {bullets.map((b) => {
          const frac = b.value == null ? 0 : Math.min(1, b.value / b.scaleMax)
          const color = bulletColor(b)
          return (
            <div key={b.key} title={b.title} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 90, fontSize: 10, color: 'var(--t-1)', flexShrink: 0 }}>{b.label}</span>
              <div style={{ flex: 1, position: 'relative', height: 8, background: 'var(--bg-3)', borderRadius: 3 }}>
                {/* watch/bad band markers at 1/3 and 2/3 of scale */}
                <div style={{ position: 'absolute', left: '33%', top: 0, bottom: 0, width: 1, background: 'var(--bd-1)' }} />
                <div style={{ position: 'absolute', left: '66%', top: 0, bottom: 0, width: 1, background: 'var(--bd-1)' }} />
                {b.value != null && (
                  <div style={{ width: `${Math.max(2, frac * 100)}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s' }} />
                )}
              </div>
              <span style={{ width: 52, fontSize: 10, fontWeight: 600, textAlign: 'right', color: b.value == null ? 'var(--t-2)' : 'var(--t-0)', fontStyle: b.value == null ? 'italic' : undefined }}>
                {b.value == null ? 'n/a' : b.fmt(b.value)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
